/* eslint-disable max-len */
import { ObservationsRegistration } from '../performance/models/performance.model';
import Utils from '../utils';
import { AnimalTransition, Cycle, Order, Section } from './models/cycle.model';

export default class CycleUtils {
  private static readonly oneDayTime = 1000 * 60 * 60 * 24;

  static getCycleStartDate(cycle: Cycle): Date {
    let earliestDate;
    for (const arrival of cycle.arrivals) {
      const birthDate = Utils.toUtcDate(new Date(arrival.arrivalDate));
      if (earliestDate === undefined || birthDate < earliestDate) {
        earliestDate = birthDate;
      }
    }
    return earliestDate;
  }

  static getCycleSectionStartDate(cycle: Cycle, sectionId: number): Date {
    let earliestSectionArrivalDate: Date;
    cycle.arrivals.forEach((arrival) => {
      arrival.allocations.forEach((allocation) => {
        if (allocation.section.id === sectionId) {
          const chickArrivalDate = Utils.toUtcDate(new Date(arrival.arrivalDate));
          if (
            earliestSectionArrivalDate === undefined ||
            chickArrivalDate < earliestSectionArrivalDate
          ) {
            earliestSectionArrivalDate = chickArrivalDate;
          }
        }
      });
    });
    return earliestSectionArrivalDate;
  }

  static getCycleSectionMovementStartDate(cycle: Cycle, sectionId: number): Date {
    let earliestSectionMovementDate: Date;
    if (!earliestSectionMovementDate) {
      cycle.movements.forEach((movement) => {
        if (movement.toSection.id === sectionId) {
          const chickArrivalDate = Utils.toUtcDate(new Date(movement.movementDate));
          if (
            earliestSectionMovementDate === undefined ||
            chickArrivalDate < earliestSectionMovementDate
          ) {
            earliestSectionMovementDate = chickArrivalDate;
          }
        }
      });
    }

    return earliestSectionMovementDate;
  }

  static getCycleEndDate(cycle: Cycle): Date {
    let latestDate;
    for (const departure of cycle.departures) {
      const departureDate = Utils.toUtcDate(new Date(departure.departureDate));
      if (latestDate === undefined || departureDate > latestDate) {
        latestDate = departureDate;
      }
    }
    return latestDate;
  }

  static getSectionStartDate(cycle: Cycle, sectionId: number): string {
    const animalTransitions = this.getAnimalTransitions(cycle, []).filter(
      (t) => t.toSectionId === sectionId,
    );
    return animalTransitions.length > 0 ? animalTransitions[0].dateString : undefined;
  }

  static getMortalityAtDate(
    cycle: Cycle,
    sectionId: number,
    date: Date,
    observations: ObservationsRegistration[],
    includeChangesAtDate: boolean,
  ): number {
    let mortality = 0;

    if (observations?.length > 0) {
      const untilRegistrationDay = this.getDayOfDateInCycle(cycle, date);
      observations
        .filter(
          (o) =>
            o.cycleId === cycle.id &&
            o.sectionId === sectionId &&
            (includeChangesAtDate ? o.day <= untilRegistrationDay : o.day < untilRegistrationDay) &&
            o.mortalityRegistration,
        )
        .forEach((o) => {
          mortality += o.mortalityRegistration.death ?? 0;
          mortality += o.mortalityRegistration.cripple ?? 0;
          mortality += o.mortalityRegistration.sexError ?? 0;
          mortality += o.mortalityRegistration.selection ?? 0;
          mortality += o.mortalityRegistration.other ?? 0;
          mortality += o.mortalityRegistration.damages ?? 0;
        });
    }

    return mortality;
  }

  static getNumberOfAnimalsInSectionAtDate(
    cycle: Cycle,
    sectionId: number,
    date: Date,
    observations: ObservationsRegistration[],
    includeChangesAtDate: boolean,
    propagationCompanyId: number,
  ): number {
    const animalTransitions = this.getAnimalTransitions(cycle, observations).filter((t) =>
      includeChangesAtDate
        ? t.date <= date
        : t.date < date && (t.fromSectionId === sectionId || t.toSectionId === sectionId),
    );

    const state = this.getState(animalTransitions);
    if (state instanceof Error) {
      return -1;
    } else {
      // In case want the numbers for a specific propagation company, only use the key for that company.
      if (propagationCompanyId) {
        return state[`${sectionId}_${propagationCompanyId}`] ?? 0;
      } else {
        // Otherwise, take all keys for the given section and take the sum.
        const keys = Object.keys(state).filter(
          (key) => sectionId === this.getSectionIdFromKey(key),
        );
        const sum = keys.map((k) => state[k]).reduce((acc: number, curr: number) => acc + curr, 0);
        return sum;
      }
    }
  }

  static getSectionIdFromKey(key: string): number {
    const keyParts = key.split('_');
    return parseInt(keyParts[0], 10);
  }

  static getNumberOfAnimalsPerPropagationCompanyAndBreedAndGender(
    cycle: Cycle,
    order: Order,
    gender: string,
    observations: ObservationsRegistration[],
  ): number {
    // 1. create array with all allocations inside all arrivals in cycle

    const sections = cycle.sections.filter(
      (s) => s.breed.id === order.breed.id && s.gender === gender,
    );

    const totalNumberOfAnimals = sections
      // 3. get the numbers of animals for filtered allocations
      .map((section: Section) => {
        const numberOfAnimals = CycleUtils.getNumberOfAnimalsInSectionAtDate(
          cycle,
          section.id,
          new Date(),
          observations,
          true,
          order.propagationCompany.id,
        );
        return numberOfAnimals;
      })
      // 4. add the numbers of animals up to one total number
      .reduce((acc: number, nrOfAnimals: number) => acc + nrOfAnimals, 0);

    return totalNumberOfAnimals;
  }

  static getAnimalTransitions(
    cycle: Cycle,
    observations: ObservationsRegistration[],
  ): AnimalTransition[] {
    const animalTransitions: AnimalTransition[] = [];
    cycle.arrivals.forEach((a) =>
      a.allocations.forEach((al) =>
        animalTransitions.push({
          dateString: a.arrivalDate,
          date: Utils.toUtcDate(new Date(a.arrivalDate)),
          toSectionId: al.section.id,
          toPropagationCompanyId: al.propagationCompany.id,
          numberOfAnimals: al.numberOfAnimals,
        }),
      ),
    );

    cycle.movements.forEach((m) =>
      animalTransitions.push({
        dateString: m.movementDate,
        date: Utils.toUtcDate(new Date(m.movementDate)),
        fromPropagationCompanyId: m.fromPropagationCompany.id,
        fromSectionId: m.fromSection.id,
        toPropagationCompanyId: m.toPropagationCompany.id,
        toSectionId: m.toSection.id,
        numberOfAnimals: m.numberOfAnimals,
      }),
    );

    cycle.departures.forEach((d) =>
      d.allocations.forEach((a) => {
        animalTransitions.push({
          dateString: d.departureDate,
          date: Utils.toUtcDate(new Date(d.departureDate)),
          fromSectionId: a.section.id,
          toPropagationCompanyId: d.propagationCompany.id,
          numberOfAnimals: a.numberOfAnimals,
        });
      }),
    );

    if (observations) {
      observations.forEach((o) => {
        let mortality = 0;
        mortality += o.mortalityRegistration.death ?? 0;
        mortality += o.mortalityRegistration.cripple ?? 0;
        mortality += o.mortalityRegistration.sexError ?? 0;
        mortality += o.mortalityRegistration.selection ?? 0;
        mortality += o.mortalityRegistration.other ?? 0;
        mortality += o.mortalityRegistration.damages ?? 0;

        if (mortality > 0) {
          // Mortality is not bound to a specific propagation company (yet). This is done when processing the state.
          const date = this.getSectionDateOfDayInCycle(cycle, o.day, o.sectionId);
          if (date) {
            animalTransitions.push({
              dateString: date.toISOString(),
              date: Utils.toUtcDate(new Date(date)),
              fromSectionId: o.sectionId,
              fromPropagationCompanyId: null,
              numberOfAnimals: mortality,
            });
          }
        }
      });
    }

    animalTransitions.sort((a, b) => (a.date.getTime() > b.date.getTime() ? 1 : -1));
    return animalTransitions;
  }

  /**
   * Create a state based on the given animalTransitions. Keys are build using <sectionId>_<propagationCompanyId>. The values are the numbers of animals.
   *
   * @param animalTransitions All animal transitions that should be used for the state.
   * @returns the state.
   */
  static getState(animalTransitions: AnimalTransition[]): Record<string, number> | Error {
    const state: Record<string, number> = {};

    animalTransitions.forEach((transition) => {
      const fromKey = `${transition.fromSectionId}_${transition.fromPropagationCompanyId}`;
      const toKey = `${transition.toSectionId}_${transition.toPropagationCompanyId}`;

      if (transition.fromSectionId) {
        if (transition.fromPropagationCompanyId) {
          if (Object.prototype.hasOwnProperty.call(state, fromKey)) {
            if (state[fromKey] >= transition.numberOfAnimals) {
              state[fromKey] -= transition.numberOfAnimals;
            } else {
              return new Error(
                `Invalid transition detected. '${fromKey}' does not have enough animals to fulfill transition of ${transition.numberOfAnimals} animals.`,
              );
            }
          } else {
            return new Error(
              `Invalid transition detected. '${fromKey}' was not part of the state.`,
            );
          }
          // This transition is coming from a mortality registration. This applies to all propagation companies in the corresponding section.
        } else {
          // Get all current keys in the state for this section.
          let mortalityLeft = transition.numberOfAnimals;
          let mortalityRegistered = 0;

          while (mortalityLeft > 0) {
            const keys = Object.keys(state).filter(
              (key) => key.startsWith(`${transition.fromSectionId}_`) && state[key] > 0,
            );
            if (keys.length > 0) {
              const mortalityPerPropagationCompany = Math.floor(mortalityLeft / keys.length);

              // In case there is mortality left, but rounding results in 0, we will just add the leftover to the last possible candidate.
              if (mortalityPerPropagationCompany === 0) {
                state[keys[keys.length - 1]] -= mortalityLeft;
                mortalityRegistered += mortalityLeft;
              } else {
                // Mortality should be equally distributed across propagation companies. In case of rounding issues, the last propagation company will get the last one.
                keys.forEach((key) => {
                  if (state[key] >= mortalityPerPropagationCompany) {
                    state[key] -= mortalityPerPropagationCompany;
                    mortalityRegistered += mortalityPerPropagationCompany;
                  } else {
                    mortalityRegistered += state[key];
                    state[key] = 0;
                  }
                });
              }

              mortalityLeft = transition.numberOfAnimals - mortalityRegistered;
            } else {
              // This should not be possible and should be validated when registering mortality. We choose to not throw exception here, since this function is called from multiple places.
              // throw Error(
              //   `Invalid transition detected. A mortality was registered when there were no animals in the section: ${transition.fromSectionId}`,
              // );
              state[`${transition.fromSectionId}`] = -mortalityLeft;
              mortalityLeft = 0;
            }
          }
        }
      }

      if (transition.toSectionId && transition.toPropagationCompanyId) {
        if (Object.prototype.hasOwnProperty.call(state, toKey)) {
          state[toKey] += transition.numberOfAnimals;
        } else {
          state[toKey] = transition.numberOfAnimals;
        }
      }
    });

    return state;
  }

  static getDateOfDayInCycle(cycle: Cycle, dayNumber: number): Date {
    const cycleStartDate = this.getCycleStartDate(cycle);
    if (cycleStartDate) {
      return Utils.toUtcDate(new Date(cycleStartDate.getTime() + dayNumber * this.oneDayTime));
    }
    return undefined;
  }

  static getSectionDateOfDayInCycle(cycle: Cycle, dayNumber: number, section: number): Date {
    const cycleSectionStartDate = this.getCycleSectionStartDate(cycle, section);
    if (cycleSectionStartDate) {
      return Utils.toUtcDate(
        new Date(cycleSectionStartDate.getTime() + dayNumber * this.oneDayTime),
      );
    } else {
      const cycleMovementStartDate = this.getCycleSectionMovementStartDate(cycle, section);
      if (cycleMovementStartDate) {
        const cycleStartDate = this.getCycleStartDate(cycle);

        return Utils.toUtcDate(new Date(cycleStartDate.getTime() + dayNumber * this.oneDayTime));
      }
    }
    return undefined;
  }

  static getDayOfDateInCycle(cycle: Cycle, date: Date): number {
    const cycleStartDate = this.getCycleStartDate(cycle);
    if (cycleStartDate) {
      return CycleUtils.getDaysDifferenceBetweenTwoDates(cycleStartDate, date);
    }
    return undefined;
  }

  static getDaysDifferenceBetweenTwoDates(date1: Date, date2: Date): number {
    return Math.ceil(
      Math.abs(Utils.toUtcDate(date2).getTime() - Utils.toUtcDate(date1).getTime()) /
        this.oneDayTime,
    );
  }

  static getYoungestChickBirthDateInSection(cycle: Cycle, sectionId: number): Date {
    const arrivals = cycle.arrivals.filter(
      (arrival) =>
        arrival.allocations.find((allocation) => allocation.section.id === sectionId) !== undefined,
    );

    if (arrivals && arrivals.length > 0) {
      const birthDates = arrivals.map((arrival) => new Date(arrival.chickBirthDate));
      return new Date(Math.min.apply(null, birthDates));
    }

    return undefined;
  }
}
